/**
 * Which URL a step on an order or a purchase order posts to (docs/features/
 * order-to-invoice/08_order_pages_frontend_spec.md §2).
 *
 * The URL is never built here: it is read from the view the page holds, which
 * carries one for each step this user may take now and null for the rest. A
 * URL from a response is trusted only if it is a path on our own API: it must
 * start with exactly one `/`, so never `//host`, `/\host` or `https:` —
 * whatever the response says. Both adapters resolve through this, so the mock
 * refuses exactly what the real adapter refuses.
 */

import type {
  ChangeDeliveryView,
  ChangeSupplierView,
  DeliveryLineCommand,
  DeliveryLineView,
  LineDespatchProgress,
  OrderCommand,
  OrderView,
  FileLinkLine,
  PlacementView,
  PurchaseOrderCommand,
  PurchaseOrderView,
} from "./order-to-invoice-types";

/** The view offers no URL for the step: this user may not take it now. */
export class OrderStepNotOfferedError extends Error {
  constructor(what: string) {
    super(`${what} is not offered: you may not take this step now.`);
    this.name = "OrderStepNotOfferedError";
  }
}

/** A response carried an action URL that is not a path on our own API. */
export class UnsafeActionUrlError extends Error {
  constructor() {
    super(
      "Refused an action URL that is not a relative path on this API (it must start with exactly one '/').",
    );
    this.name = "UnsafeActionUrlError";
  }
}

/** The URL itself, once it is known to be a relative API path. */
export function assertRelativeActionUrl(url: string): string {
  if (!url.startsWith("/") || url.startsWith("//") || url.startsWith("/\\")) {
    throw new UnsafeActionUrlError();
  }
  return url;
}

function findDeliveryLine(
  lines: Array<{ delivery_lines: DeliveryLineView[] }>,
  deliveryLineId: string,
): DeliveryLineView | null {
  for (const line of lines) {
    const found = line.delivery_lines.find((d) => d.id === deliveryLineId);
    if (found) return found;
  }
  return null;
}

function deliveryLineUrl(
  lines: Array<{
    delivery_lines: DeliveryLineView[];
    despatch_progress?: LineDespatchProgress | null;
  }>,
  command: DeliveryLineCommand,
): string {
  const line = findDeliveryLine(lines, command.deliveryLineId);
  // Once a line has deliveries its step sits on each delivery (orders' #2748), at the same delivery-line route.
  const onDelivery = lines
    .flatMap((l) => l.despatch_progress?.deliveries ?? [])
    .find(
      (row) =>
        row.record?.delivery_line_id === command.deliveryLineId &&
        row.record.kind === command.kind,
    )?.record?.url;
  const url =
    (command.kind === "record_delivered"
      ? line?.record_delivered_url
      : line?.record_received_url) ?? onDelivery;
  if (!url) {
    throw new OrderStepNotOfferedError(
      command.kind === "record_delivered"
        ? "Record delivered"
        : "Record received",
    );
  }
  return assertRelativeActionUrl(url);
}

/** Where Record placed posts for `placement`; throws when the page offers none. */
export function recordPlacedUrl(placement: PlacementView): string {
  if (!placement.record_placed_url) {
    throw new OrderStepNotOfferedError("Record placed");
  }
  return assertRelativeActionUrl(placement.record_placed_url);
}

/** Where *Copy file link* posts for `line`; throws when the page offers none (not released, or not yours to issue). */
export function issueFileLinkUrl(line: FileLinkLine): string {
  if (!line.issue_file_link_url) {
    throw new OrderStepNotOfferedError("Copy file link");
  }
  return assertRelativeActionUrl(line.issue_file_link_url);
}

/** The URL `command` posts to on `order`; throws when the order offers none. */
export function orderCommandUrl(
  order: OrderView,
  command: OrderCommand,
): string {
  switch (command.kind) {
    case "add_extra":
      if (!order.add_extra) throw new OrderStepNotOfferedError("Add extra");
      return assertRelativeActionUrl(order.add_extra.url);
    case "add_to_project":
      if (!order.add_to_project_url)
        throw new OrderStepNotOfferedError("Add to project");
      return assertRelativeActionUrl(order.add_to_project_url);
    case "log_contact": {
      // A call with the customer is logged where the box's customer contact says.
      const logUrl = order.actions.customer_contact?.log_url;
      if (!logUrl) throw new OrderStepNotOfferedError("Log a call");
      return assertRelativeActionUrl(logUrl);
    }
    case "log_supplier_call": {
      // A supplier's call is logged on its purchase order, where that supplier's contact party says.
      const logUrl = order.actions.contacts.find(
        (c) => c.supplier_no === command.supplierNo,
      )?.log_url;
      if (!logUrl) throw new OrderStepNotOfferedError("Log a call");
      return assertRelativeActionUrl(logUrl);
    }
    case "change_payment_method":
      if (!order.payment?.change_method_url)
        throw new OrderStepNotOfferedError("Change payment method");
      return assertRelativeActionUrl(order.payment.change_method_url);
    case "set_contact":
      if (!order.set_contact_url)
        throw new OrderStepNotOfferedError("Set contact");
      return assertRelativeActionUrl(order.set_contact_url);
    case "record_customer_order_number":
      if (!order.record_customer_order_number_url)
        throw new OrderStepNotOfferedError("Record PO number");
      return assertRelativeActionUrl(order.record_customer_order_number_url);
    case "set_vat_treatment": {
      const held = order.billing?.not_yet_invoiced
        .flatMap((row) => row.held_lines)
        .find((line) => line.invoice_line_id === command.invoiceLineId);
      if (!held?.set_vat_treatment_url)
        throw new OrderStepNotOfferedError("Set VAT");
      return assertRelativeActionUrl(held.set_vat_treatment_url);
    }
    case "record_proforma_payment": {
      const url = order.billing?.proformas.find(
        (p) => p.proforma_no === command.proformaNo,
      )?.record_payment_url;
      if (!url) throw new OrderStepNotOfferedError("Record payment");
      return assertRelativeActionUrl(url);
    }
    case "mark_excess_refunded": {
      const url = order.billing?.proformas.find(
        (p) => p.proforma_no === command.proformaNo,
      )?.mark_excess_refunded_url;
      if (!url) throw new OrderStepNotOfferedError("Mark the excess refunded");
      return assertRelativeActionUrl(url);
    }
    case "resend_order_email": {
      const url = order.billing?.proformas.find(
        (p) => p.proforma_no === command.proformaNo,
      )?.resend_email_url;
      if (!url) throw new OrderStepNotOfferedError("Resend");
      return assertRelativeActionUrl(url);
    }
    case "cancel_line": {
      const url = order.lines.find(
        (l) => l.line_ref === command.lineRef,
      )?.cancel_url;
      if (!url) throw new OrderStepNotOfferedError("Cancel line");
      return assertRelativeActionUrl(url);
    }
    case "set_version_labels": {
      const url = order.lines.find(
        (l) => l.line_ref === command.lineRef,
      )?.set_version_labels_url;
      if (!url) throw new OrderStepNotOfferedError("Name versions");
      return assertRelativeActionUrl(url);
    }
    case "record_tharstern_job": {
      const url = order.lines.find(
        (l) => l.line_ref === command.lineRef,
      )?.record_tharstern_job_url;
      if (!url) throw new OrderStepNotOfferedError("Record Tharstern job");
      return assertRelativeActionUrl(url);
    }
    case "agree_delivery_price": {
      const url = order.delivery_groups.find((g) => g.id === command.groupId)
        ?.charge?.agree_price_url;
      if (!url) throw new OrderStepNotOfferedError("Agree price");
      return assertRelativeActionUrl(url);
    }
    case "record_despatched_together": {
      const url = order.delivery_groups.find((g) => g.id === command.groupId)
        ?.record_despatched_together?.url;
      if (!url) throw new OrderStepNotOfferedError("Record despatched");
      return assertRelativeActionUrl(url);
    }
    case "book_courier_despatch": {
      const url = order.delivery_groups.find((g) => g.id === command.groupId)
        ?.record_despatched_together?.book_courier_url;
      if (!url) throw new OrderStepNotOfferedError("Book with courier");
      return assertRelativeActionUrl(url);
    }
    case "record_made_despatched": {
      const url = order.delivery_groups
        .flatMap((g) => g.record_made_despatched ?? [])
        .find(
          (a) =>
            a.line_ref === command.lineRef &&
            a.delivery_line_id === command.deliveryLineId,
        )?.url;
      if (!url) throw new OrderStepNotOfferedError("Record despatched");
      return assertRelativeActionUrl(url);
    }
    case "release_lines": {
      // Every line in the request must offer Release; they share orders' one URL. No line offers none.
      const urls = (command.input.lines ?? []).map(
        (item) =>
          order.lines.find((l) => l.line_ref === item.line_ref)?.release
            ?.release_url,
      );
      const url = urls[0];
      if (!url || urls.some((u) => !u))
        throw new OrderStepNotOfferedError("Release");
      return assertRelativeActionUrl(url);
    }
    case "hold_release": {
      const url = releaseOf(order, command.lineRef)?.hold_url;
      if (!url) throw new OrderStepNotOfferedError("Hold");
      return assertRelativeActionUrl(url);
    }
    case "place_now": {
      const url = releaseOf(order, command.lineRef)?.place_now_url;
      if (!url) throw new OrderStepNotOfferedError("Place now");
      return assertRelativeActionUrl(url);
    }
    case "set_review_before_release":
      if (!order.release_review?.set_url)
        throw new OrderStepNotOfferedError("Review before release");
      return assertRelativeActionUrl(order.release_review.set_url);
    case "set_deliver_together":
      if (!order.deliver_together_switch?.set_url)
        throw new OrderStepNotOfferedError("Deliver together");
      return assertRelativeActionUrl(order.deliver_together_switch.set_url);
    case "change_supplier": {
      const url = releaseOf(order, command.lineRef)?.change_supplier
        ?.action_url;
      if (!url) throw new OrderStepNotOfferedError("Change supplier");
      return assertRelativeActionUrl(url);
    }
    case "change_delivery": {
      const url = order.lines.find((l) => l.line_ref === command.lineRef)
        ?.change_delivery?.action_url;
      if (!url) throw new OrderStepNotOfferedError("Change delivery");
      return assertRelativeActionUrl(url);
    }
    case "record_delivered":
    case "record_received":
      return deliveryLineUrl(order.lines, command);
  }
}

/** Where *Change delivery…* previews the change; throws when the view offers none. */
export function deliveryChangePreviewUrl(view: ChangeDeliveryView): string {
  return assertRelativeActionUrl(view.preview_url);
}

function releaseOf(order: OrderView, lineRef: string) {
  return order.lines.find((l) => l.line_ref === lineRef)?.release ?? null;
}

/** Where *Change supplier…* reads its choices; throws when the view offers none (a bought service, priced by hand). */
export function supplierAlternativesUrl(view: ChangeSupplierView): string {
  if (!view.alternatives_url)
    throw new OrderStepNotOfferedError("Change supplier's alternatives");
  return assertRelativeActionUrl(view.alternatives_url);
}

/**
 * Where a bought service's supplier is searched (`add_extra.suppliers_url`,
 * or a service's `change_supplier.suppliers_url`), with `q`; throws when the
 * URL is missing or not a relative API path.
 */
export function serviceSuppliersUrl(
  suppliersUrl: string | null | undefined,
  q: string,
): string {
  if (!suppliersUrl)
    throw new OrderStepNotOfferedError("Search service suppliers");
  const base = assertRelativeActionUrl(suppliersUrl);
  const typed = q.trim();
  return typed ? `${base}?${new URLSearchParams({ q: typed })}` : base;
}

/** The URL Copy pay link posts to on `order`; throws when the order offers no card payment to link. */
export function orderPayLinkUrl(order: OrderView): string {
  if (!order.payment?.pay_link_url)
    throw new OrderStepNotOfferedError("Copy pay link");
  return assertRelativeActionUrl(order.payment.pay_link_url);
}

/** The URL `command` posts to on `purchaseOrder`; throws when it offers none. */
export function purchaseOrderCommandUrl(
  purchaseOrder: PurchaseOrderView,
  command: PurchaseOrderCommand,
): string {
  if (command.kind === "record_cancellation") {
    // Null when this caller may not cancel it now (a held order is a manager's).
    const url = purchaseOrder.record_cancellation?.url;
    if (!url) throw new OrderStepNotOfferedError("Record cancellation");
    return assertRelativeActionUrl(url);
  }
  if (command.kind === "record_despatch") {
    const url = purchaseOrder.record_despatch?.url;
    if (!url) throw new OrderStepNotOfferedError("Record despatch");
    return assertRelativeActionUrl(url);
  }
  if (command.kind === "approve_price") {
    // Null unless the order is held for price approval and the caller is a manager or admin.
    const url = purchaseOrder.approve_price_url;
    if (!url) throw new OrderStepNotOfferedError("Approve price");
    return assertRelativeActionUrl(url);
  }
  if (command.kind === "record_agreed_amount") {
    const url = purchaseOrder.lines.find((l) => l.line_no === command.lineNo)
      ?.record_agreed_amount?.url;
    if (!url) throw new OrderStepNotOfferedError("Record agreed amount");
    return assertRelativeActionUrl(url);
  }
  if (command.kind === "resend_order_email") {
    const url = purchaseOrder.sent_to_supplier?.resend_url;
    if (!url) throw new OrderStepNotOfferedError("Resend");
    return assertRelativeActionUrl(url);
  }
  if (command.kind === "revoke_order_links") {
    const url = purchaseOrder.sent_to_supplier?.revoke_url;
    if (!url) throw new OrderStepNotOfferedError("Revoke link");
    return assertRelativeActionUrl(url);
  }
  if (command.kind === "decide_date_change") {
    const line = purchaseOrder.lines.find((l) => l.line_no === command.lineNo);
    const url = line?.date_proposal?.decide_url;
    if (!url) throw new OrderStepNotOfferedError("Decide the new date");
    return assertRelativeActionUrl(url);
  }
  if (command.kind === "decide_supplier_proof") {
    const proof = purchaseOrder.lines
      .flatMap((l) => l.proofs)
      .find((p) => p.id === command.proofId);
    if (!proof?.decide_url)
      throw new OrderStepNotOfferedError("Decide the proof");
    return assertRelativeActionUrl(proof.decide_url);
  }
  return deliveryLineUrl(purchaseOrder.lines, command);
}

/**
 * The step that completes a delivery line: Record delivered to a customer's
 * address, Record received into a warehouse; `requestId` is the dialog's
 * idempotency key.
 */
export function recordCommand(
  line: DeliveryLineView,
  {
    quantity,
    on,
    requestId,
  }: { quantity: number; on: string; requestId: string },
): DeliveryLineCommand {
  return line.destination.kind === "warehouse"
    ? {
        kind: "record_received",
        deliveryLineId: line.id,
        input: { quantity, received_on: on, request_id: requestId },
      }
    : {
        kind: "record_delivered",
        deliveryLineId: line.id,
        input: { quantity, delivered_on: on, request_id: requestId },
      };
}
